import constants from '@/constants'
import React, { useState, useEffect } from 'react'
import { selectExchangeRate, selectExchangeRateStatus } from '@/store/app'
import { useSelector } from 'react-redux'
import { selectCurrencyProproties } from '@/store/account'
// Only import icons that are always needed
import { OomsIcon, RealMoneyIcon, BtcIcon } from '@/assets/icons'
// Use centralized icon loader for other currencies
import { getCurrencyIcon } from '@/util/currencyIcons.jsx'
// Import shared utility functions
import {
  parseCurrencyDecimalPlaces,
  getCurrencyParameter,
  getMarketType,
  convertFromCanonical,
  convertToCanonical,
} from '@/util/currencyUtils'

const useCurrencies = () => {
  const exchangeRate = useSelector(selectExchangeRate)
  const currencyProproties = useSelector(selectCurrencyProproties())
  const exchangeRateStatus = useSelector(selectExchangeRateStatus())

  const oomsCurrency = {
    id: 'OOM',
    label: 'Ooms',
    general_label: 'Play money',
    currencySymbol: <OomsIcon width="1em" />,
    display_ratio: 1,
    display_decimal_places: 2,
    display_suffix: 'ø',
    display_code: 'Ooms',
    defaultBet: 100,
    default_bet_amount: 100,
    mainColor: '#1591ed',
  }
  const [OOM, setOOM] = useState(oomsCurrency)
  const cryptoCurrencies = [...Object.keys(currencyProproties.currencies)].filter(
    key => !['OOM', 'USD', 'EUR'].includes(key)
  )
  const isCryptoCurrency = currencyId => {
    return cryptoCurrencies.indexOf(currencyId) >= 0
  }
  useEffect(() => {
    // The backend's OOM entry carries a static `icon` (raster PNG) used for crypto
    // currencies. CurrencyIcon prefers `icon` over `currencySymbol` when both are
    // present, which would replace the themed OomsIcon SVG with that flat PNG —
    // breaking dark mode and brand-04 theming. Drop it so OOM always renders the SVG.
    setOOM(oom => ({ ...oom, ...currencyProproties.currencies.OOM, icon: undefined }))
  }, [currencyProproties.currencies])

  // Use centralized icon loader - icons are loaded on-demand
  const getIcon = (currencyId, isDefault) => {
    return getCurrencyIcon(currencyId, isDefault)
  }
  let realMoneyCurrencies = {
    ...Object.fromEntries(
      Object.entries(currencyProproties.currencies).filter(
        ([key]) => !['OOM'].includes(key) && currencyProproties.currencies[key]
      )
    ),
  }

  Object.keys(realMoneyCurrencies).forEach(_currency => {
    realMoneyCurrencies[_currency] = {
      ...realMoneyCurrencies[_currency],
      currencySymbol: getIcon(_currency),
      defaultCurrencySymbol: getIcon(_currency, true),
      id: _currency,
    }
  })

  const realMoneyPlaceholder = {
    id: constants.REAL_MONEY_PLACEHOLDER,
    label: 'Real Money',
    symbol: <RealMoneyIcon width="1em" fill="currentColor" />,
    currencySymbol: <RealMoneyIcon />,
    display_code: 'Real Money',
    display_ratio: 1,
    display_decimal_places: 2,
    default_bet_amount: 5,
    display_prefix: '$',
  }
  const testCoin = {
    id: 'TEST',
    label: 'TestCoin',
    symbol: <span style={{ fontFamily: 'Encode Sans' }}>Test</span>,
    currencySymbol: <span>Test</span>,
    display_code: 'TC',
    display_ratio: 0.001,
    display_decimal_places: 8,
    icon: BtcIcon,
    mainColor: '#F7931A',
    defaultBet: 0.0002,
    default_bet_amount: 0.0002,
  }

  // Currency-specific implementations
  const isPlaceHolder = currencyId => currencyId === constants.REAL_MONEY_PLACEHOLDER
  const isRealMoney = currencyId => 
    currencyId && currencyId !== oomsCurrency.id && !isPlaceHolder(currencyId)
  const parseCurrencyParameter = currencyId => 
    getCurrencyParameter(isRealMoney(currencyId))
  const getCurrencyMode = currencyId => getMarketType(isRealMoney(currencyId))

  return {
    oomsCurrency,
    testCoin,
    blockchains: currencyProproties.blockchains,
    realMoneyCurrencies,
    status: exchangeRateStatus,
    currencies: {
      ...realMoneyCurrencies,
      OOM: { ...OOM },
      [constants.REAL_MONEY_PLACEHOLDER]: realMoneyPlaceholder,
    },
    aggregatedCurrencies: {
      ...realMoneyCurrencies,
      OOM: { ...OOM },
      [constants.REAL_MONEY_PLACEHOLDER]: realMoneyPlaceholder,
    },
    CANONICAL: { ...currencyProproties.currencies['BTC'] },
    [constants.REAL_MONEY_PLACEHOLDER]: realMoneyPlaceholder,
    OOM: { ...OOM },
    parseCurrencyDecimalPlaces: parseCurrencyDecimalPlaces,
    isRealMoney: isRealMoney,
    parseCurrencyParameter: parseCurrencyParameter,
    getCurrencyMode: getCurrencyMode,
    getMarketType: getMarketType,
    convertToCanonical: convertToCanonical,
    convertFromCanonical: convertFromCanonical,
    exchangeRate: exchangeRate,
    isCryptoCurrency: isCryptoCurrency,
  }
}

export default useCurrencies
